import { useCallback, useEffect, useRef, useState } from 'react'
import type { Navigate } from '../App'
import { useStore } from '../state/store'
import { onVoices, speechSupported, type VoiceOption } from '../lib/speech'
import { languageOf } from '../lib/languages'
import { SpeakButton } from '../components/SpeakButton'
import { backupFilename, exportJson, importJson } from '../lib/storage'
import { buildSeedIslands } from '../data/seedIslands'
import { levelFor, levelName, scaleStep, stepLabel, tierFor } from '../lib/levels'
import { buildSeedScenarios } from '../data/seedScenarios'
import { listDevices, revokeDevice, type DeviceRow } from '../lib/api'
import { collectDirty } from '../lib/sync'
import { isInstalled, onInstallAvailable, promptInstall } from '../lib/pwa'
import { BillingCard } from '../components/BillingCard'
import { AiKeyCard } from '../components/AiKeyCard'

/** A short line in each language, for testing the chosen voice. */
const TEST_PHRASE: Record<string, string> = {
  zh: '你好，我在练习中文。',
  de: 'Guten Tag, ich übe gerade Deutsch.',
  fr: "Bonjour, je m'entraîne en français.",
  es: 'Hola, estoy practicando español.',
  it: 'Buongiorno, sto praticando l’italiano.',
}

export function SettingsView({
  navigate,
  standing = null,
  billing = null,
}: {
  navigate: Navigate
  /** How this site's language is held, for the subscription card. */
  standing?: { source: string; expiresAt: number | null } | null
  /** What Stripe sent us back with, for the subscription card to acknowledge. */
  billing?: 'done' | 'cancelled' | 'subscribe' | null
}) {
  const { data, dispatch } = useStore()
  const settings = data.settings
  const [voices, setVoices] = useState<VoiceOption[]>([])
  const [message, setMessage] = useState<{ text: string; error?: boolean } | null>(null)
  const [devices, setDevices] = useState<DeviceRow[] | null>(null)
  const [canInstall, setCanInstall] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => onInstallAvailable(setCanInstall), [])

  const token = data.sync.token
  const pending = token ? collectDirty(data).length : 0

  // Voices are per language: one selection cannot serve five of them.
  const activeLang = languageOf(data.settings.activeLanguage)
  const current = levelFor(data.settings, data.settings.activeLanguage)
  useEffect(() => onVoices(activeLang.speech, setVoices), [activeLang.speech])

  const loadDevices = useCallback(async () => {
    if (!token) return
    const result = await listDevices(token)
    if (result.ok) setDevices(result.devices)
    else setDevices([])
  }, [token])

  useEffect(() => {
    void loadDevices()
  }, [loadDevices])

  const patch = (fields: Partial<typeof settings>) =>
    dispatch({ type: 'updateSettings', patch: fields })

  const download = () => {
    const blob = new Blob([exportJson(data)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = backupFilename()
    a.click()
    URL.revokeObjectURL(url)
    setMessage({ text: 'Exported. Keep it somewhere you back up.' })
  }

  const upload = async (file: File) => {
    const result = importJson(await file.text())
    if (!result.ok || !result.data) {
      setMessage({ text: result.error ?? 'Import failed.', error: true })
      return
    }
    const ok = window.confirm(
      `Replace everything currently in this browser with the contents of ${file.name}? Your ${data.islands.length} islands and ${data.history.length} logged sessions will be overwritten.`,
    )
    if (!ok) return
    dispatch({ type: 'replaceAll', data: result.data })
    setMessage({ text: `Imported ${result.data.islands.length} islands.` })
  }

  return (
    <>
      <header className="page-head">
        <div className="eyebrow">Settings</div>
        <h1 className="page-title">
          <span className="page-title-zh">{activeLang.ui.settings}</span>
        </h1>
      </header>

      {message && (
        <div className={`notice ${message.error ? 'is-error' : ''}`} style={{ marginBottom: '1rem' }}>
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          On this phone
        </div>
        {isInstalled() ? (
          <p className="english" style={{ margin: 0 }}>
            Running from the home screen. Drills work with no signal, and anything you do offline
            syncs the next time you have one.
          </p>
        ) : canInstall ? (
          <>
            <p className="english" style={{ marginTop: 0 }}>
              Install it to get an icon on your home screen and full-screen drills without the
              browser bar.
            </p>
            <button
              type="button"
              className="btn is-primary"
              onClick={async () => {
                const outcome = await promptInstall()
                if (outcome === 'dismissed') {
                  setMessage({ text: 'Left it in the browser. You can install any time.' })
                }
              }}
            >
              Install on this device
            </button>
          </>
        ) : (
          <p className="english" style={{ margin: 0 }}>
            To install: open the browser menu and choose <strong>Add to Home screen</strong>. Chrome
            on Android offers it once the page has been open a moment.
          </p>
        )}
      </div>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Sync
        </div>

        {!token ? (
          <>
            <p className="english" style={{ marginTop: 0 }}>
              This device is working on its own. Its islands live here and nowhere else — connect it
              to share them with your phone.
            </p>
            <button
              type="button"
              className="btn is-primary"
              onClick={() => navigate({ view: 'signin' })}
            >
              Connect this device
            </button>
          </>
        ) : (
          <>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span>
                Signed in as <strong>{data.sync.accountEmail ?? 'this account'}</strong>
                <span className="muted"> on {data.sync.deviceName}</span>
              </span>
              <span className={`badge ${pending > 0 ? 'is-warming' : 'is-ready'}`}>
                {pending > 0 ? `${pending} waiting to send` : 'up to date'}
              </span>
            </div>

            {data.sync.lastError && (
              <div className="notice" style={{ marginTop: '0.7rem' }}>
                {data.sync.lastError}
              </div>
            )}

            <div className="divider" />

            <div className="stat-label" style={{ marginBottom: '0.4rem' }}>
              Devices
            </div>

            {devices === null ? (
              <p className="muted" style={{ fontSize: '0.86rem' }}>
                Checking…
              </p>
            ) : devices.length === 0 ? (
              <p className="muted" style={{ fontSize: '0.86rem' }}>
                Couldn't reach the server. The list will fill in when you're back online.
              </p>
            ) : (
              devices.map((device) => (
                <div key={device.id} className="block-row" style={{ alignItems: 'center' }}>
                  <span style={{ flex: 1 }}>
                    {device.name}
                    {device.isCurrent && <span className="badge" style={{ marginLeft: '0.5rem' }}>this one</span>}
                  </span>
                  {/* No "revoked" badge, because a revoked device is gone. The
                      list is only the sessions that can still reach your
                      account, which is the one question it exists to answer. */}
                  {!device.isCurrent && (
                    <button
                      type="button"
                      className="btn is-small is-danger"
                      onClick={async () => {
                        const ok = window.confirm(
                          `Sign "${device.name}" out? It stops syncing immediately and disappears from this list, but keeps whatever is already on it.`,
                        )
                        if (!ok || !token) return
                        const result = await revokeDevice(device.id, token)
                        setMessage(
                          result.ok
                            ? { text: `Revoked ${device.name}.` }
                            : { text: result.error, error: true },
                        )
                        void loadDevices()
                      }}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))
            )}

            <div className="divider" />

            <button
              type="button"
              className="btn is-small"
              onClick={() => {
                const ok = window.confirm(
                  pending > 0
                    ? `Sign out with ${pending} changes not yet sent? They stay on this device and will go up when you sign back in.`
                    : 'Sign this device out of sync? Its islands stay here.',
                )
                if (ok) dispatch({ type: 'signOut' })
              }}
            >
              Sign out
            </button>
          </>
        )}
      </div>

      <BillingCard standing={standing} billing={billing} />

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Voice — {activeLang.name}
        </div>

        {!speechSupported() ? (
          <div className="notice is-error">
            This browser has no speech synthesis, so playback is unavailable. Chrome, Edge, and
            Safari all support it.
          </div>
        ) : voices.length === 0 ? (
          <div className="notice">
            No {activeLang.name} voice is installed, so playback will not work for it. On Windows:
            Settings → Time &amp; language → Language &amp; region → add {activeLang.nativeName},
            open its language options, and install the speech pack. Restart the browser afterwards.
          </div>
        ) : (
          <>
            <label className="field">
              <span className="field-label">Speaking voice</span>
              <select
                className="select"
                value={settings.voiceByLanguage?.[activeLang.code] ?? ''}
                onChange={(e) =>
                  patch({
                    voiceByLanguage: {
                      ...settings.voiceByLanguage,
                      [activeLang.code]: e.target.value || null,
                    },
                  })
                }
              >
                <option value="">Automatic — first {activeLang.name} voice</option>
                {voices.map((v) => (
                  <option key={v.uri} value={v.uri}>
                    {v.name} ({v.lang})
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span className="field-label">Speed — {settings.speechRate.toFixed(2)}×</span>
              <input
                type="range"
                min={0.3}
                max={1.3}
                step={0.05}
                value={settings.speechRate}
                onChange={(e) => patch({ speechRate: Number(e.target.value) })}
              />
            </label>

            <label className="field">
              <span className="field-label">Slower — {settings.slowRate.toFixed(2)}×</span>
              <input
                type="range"
                min={0.3}
                max={1}
                step={0.05}
                value={settings.slowRate}
                onChange={(e) => patch({ slowRate: Number(e.target.value) })}
              />
              <span className="muted" style={{ fontSize: '0.84rem' }}>
                What the “Slower” button beside each line uses. Below about 0.4× most browser
                voices slur rather than separate words — usually a fault, but often what you want
                when shadowing, since it buys room between the syllables.
              </span>
            </label>

            <div className="row">
              {/* The same component every other screen uses. Hand-rolled copies
                  here discarded speak()'s result, so a missing voice was a dead
                  button on the one screen for choosing a voice. */}
              <SpeakButton
                text={TEST_PHRASE[activeLang.code] ?? 'Hello.'}
                language={activeLang.code}
                className="btn is-small"
                withSlow
              />
            </div>
          </>
        )}
      </div>

      {/* Signed in only: a key belongs to an account, and there is nowhere to
          put one without one. */}
      {token && <AiKeyCard token={token} />}

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Your level
        </div>

        {/*
          The same six steps as the rail, not the three tiers the sign-up form
          asks. That question is "who are you", answered once by someone who
          has not seen the product; this is the setting itself, and it should
          offer the scale a learner actually thinks in.
        */}
        <div className="level-steps" style={{ marginBottom: '0.7rem', maxWidth: '18rem' }}>
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <button
              key={n}
              type="button"
              className={`level-step ${n === current ? 'is-on' : ''}`}
              aria-pressed={n === current}
              aria-label={`${levelName(activeLang.code)} ${stepLabel(activeLang.code, n)}`}
              onClick={() =>
                // Per language. Answering for German must not touch Mandarin.
                patch({
                  levelByLanguage: { ...data.settings.levelByLanguage, [activeLang.code]: n },
                })
              }
            >
              {stepLabel(activeLang.code, n)}
            </button>
          ))}
        </div>

        <p className="muted" style={{ fontSize: '0.86rem', margin: 0 }}>
          {scaleStep(activeLang.code, current)} — {tierFor(current).blurb}
        </p>
        {/* Said plainly because the obvious fear on reading a "level" control is
            that it locks something away, and this one never does. */}
        <p className="muted" style={{ fontSize: '0.84rem', margin: '0.5rem 0 0' }}>
          Your level in {activeLang.name}, kept separately from the other languages. It decides
          which islands come first and what Claude aims at when it drafts one for you, and it
          hides nothing: harder starter islands move down the list and stay openable. Islands you
          built or generated yourself are never moved.
        </p>
      </div>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Drilling
        </div>

        {activeLang.modes.length > 1 && (
        <label className="field">
          <span className="field-label">Default typing mode</span>
          <select
            className="select"
            value={settings.inputMode}
            onChange={(e) => patch({ inputMode: e.target.value as 'hanzi' | 'pinyin' })}
          >
            <option value="hanzi">汉字 — characters, using your IME</option>
            <option value="pinyin">pīnyīn — tone numbers or marks</option>
          </select>
        </label>
        )}

        {activeLang.hasPhonetics && (
        <label className="checkline">
          <input
            type="checkbox"
            checked={settings.requireTones}
            onChange={(e) => patch({ requireTones: e.target.checked })}
          />
          <span>
            Require tones in pinyin mode
            <span className="muted" style={{ fontSize: '0.84rem' }}>
              {' '}
              — off gives full credit for a tone slip, but still marks it
            </span>
          </span>
        </label>
        )}

        <div className="grid grid-2" style={{ marginTop: '0.9rem' }}>
          <label className="field">
            <span className="field-label">
              Perform — seconds per block ({settings.performSecondsPerBlock}s)
            </span>
            <input
              type="range"
              min={5}
              max={60}
              step={1}
              value={settings.performSecondsPerBlock}
              onChange={(e) => patch({ performSecondsPerBlock: Number(e.target.value) })}
            />
          </label>

          <label className="field">
            <span className="field-label">Ambush — seconds to answer ({settings.ambushSeconds}s)</span>
            <input
              type="range"
              min={5}
              max={45}
              step={1}
              value={settings.ambushSeconds}
              onChange={(e) => patch({ ambushSeconds: Number(e.target.value) })}
            />
          </label>
        </div>
      </div>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Appearance
        </div>
        <div className="row">
          <div className="toggle-group">
            {(['dark', 'light'] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={`btn is-small ${settings.theme === t ? 'is-on' : ''}`}
                onClick={() => patch({ theme: t })}
              >
                {t}
              </button>
            ))}
          </div>
          <label className="checkline">
            <input
              type="checkbox"
              checked={settings.reducedMotion}
              onChange={(e) => patch({ reducedMotion: e.target.checked })}
            />
            <span>Reduce motion</span>
          </label>
        </div>
      </div>

      <div className="card">
        <div className="stat-label" style={{ marginBottom: '0.7rem' }}>
          Your data
        </div>
        <p className="english" style={{ marginTop: 0 }}>
          Everything lives in this browser's local storage and never leaves the machine. Clearing
          site data wipes it, so export if the islands matter to you.
        </p>

        <div className="row">
          <button type="button" className="btn" onClick={download}>
            Export {data.islands.length} islands
          </button>
          <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
            Import a backup
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void upload(file)
              e.target.value = ''
            }}
          />
        </div>

        <div className="divider" />

        <div className="row">
          <button
            type="button"
            className="btn is-small"
            onClick={() => {
              const existing = new Set(data.islands.map((i) => i.id))
              const missing = buildSeedIslands().filter((i) => !existing.has(i.id))
              missing.forEach((island) => dispatch({ type: 'upsertIsland', island }))

              const haveScenarios = new Set(data.scenarios.map((s) => s.id))
              const missingScenarios = buildSeedScenarios().filter((s) => !haveScenarios.has(s.id))
              missingScenarios.forEach((scenario) =>
                dispatch({ type: 'upsertScenario', scenario }),
              )

              setMessage({
                text:
                  missing.length + missingScenarios.length === 0
                    ? 'The starter set is already here — nothing to restore.'
                    : `Restored ${missing.length} islands and ${missingScenarios.length} scenarios. Anything you had edited was left alone.`,
              })
            }}
          >
            Restore missing starter content
          </button>

          <button
            type="button"
            className="btn is-small is-danger"
            style={{ marginLeft: 'auto' }}
            onClick={() => {
              const ok = window.confirm(
                `Erase everything and start over? This deletes ${data.islands.length} islands and ${data.history.length} logged sessions from this browser. Export first if you want them back.`,
              )
              if (ok) {
                dispatch({ type: 'reset' })
                setMessage({ text: 'Reset to the starter set.' })
              }
            }}
          >
            Erase everything
          </button>
        </div>
      </div>
    </>
  )
}
